import { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, CircularProgress, Avatar, Alert,
} from '@mui/material';
import {
  Business as ClientsIcon,
  CheckCircle as ActiveIcon,
  Work as JobTitlesIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { canSeeErpSection } from '../erpAccess';
import ErpTraceabilitySearch from './ErpTraceabilitySearch';

const ErpHomeLayout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, organization, organizationName, authToken, erpRole } = useStore.getState();

  // Roles ERP v2 (matriz de accesos): el home se adapta a lo que el rol ve
  const canClients = canSeeErpSection(user, erpRole, 'clients');
  const canQuotations = canSeeErpSection(user, erpRole, 'quotations');
  const canTrace = canSeeErpSection(user, erpRole, 'export');

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({ clients: 0, activeClients: 0, jobTitles: 0 });

  const userName = user?.name || user?.displayName || '';

  useEffect(() => {
    const loadMetrics = async () => {
      if (!organization || !canClients) { setLoading(false); return; }
      try {
        setLoading(true);

        const [clientsRes, jobTitlesRes] = await Promise.all([
          api.get('/erp/clients', {
            params: { organizationId: organization },
            headers: { Authorization: `Bearer ${authToken}` },
          }),
          api.get('/erp/job-titles', {
            params: { organizationId: organization },
            headers: { Authorization: `Bearer ${authToken}` },
          }),
        ]);

        const clients = clientsRes.data.response?.data || [];
        const jobTitles = jobTitlesRes.data.response?.data || [];

        setMetrics({
          clients: clients.length,
          activeClients: clients.filter((c) => c.status === 'active').length,
          jobTitles: jobTitles.length,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const metricCards = [
    { key: 'clients', value: metrics.clients, label: t('erp.home.metrics.clients'), icon: <ClientsIcon />, color: '#6A5AF9' },
    { key: 'activeClients', value: metrics.activeClients, label: t('erp.home.metrics.activeClients'), icon: <ActiveIcon />, color: '#10B981' },
    { key: 'jobTitles', value: metrics.jobTitles, label: t('erp.home.metrics.jobTitles'), icon: <JobTitlesIcon />, color: '#0EA5E9' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          {userName ? t('erp.home.welcomeName', { name: userName }) : t('erp.home.welcome')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('erp.home.subtitle')}{organizationName ? ` — ${organizationName}` : ''}
        </Typography>
      </Box>

      {/* Compras: sus módulos aún no existen — solo el aviso */}
      {!canClients && !canQuotations && (
        <Alert severity="info" sx={{ mb: 4 }}>
          {t('erp.home.underConstruction')}
        </Alert>
      )}

      {canClients && (
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {metricCards.map((m) => (
          <Grid item xs={12} sm={4} key={m.key}>
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: `${m.color}1A`, color: m.color }}>{m.icon}</Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {loading ? <CircularProgress size={20} /> : m.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{m.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      )}

      {(canClients || canQuotations) && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            {t('erp.home.quickActions.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {canClients && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/erp/clients/new')}
                  sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
                >
                  {t('erp.home.quickActions.newClient')}
                </Button>
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/erp/clients')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('erp.home.quickActions.viewClients')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<JobTitlesIcon />}
                  onClick={() => navigate('/erp/job-titles')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('erp.jobTitles.title')}
                </Button>
              </>
            )}
            {canQuotations && (
              <>
                <Button
                  variant={canClients ? 'outlined' : 'contained'}
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/erp/quotations/new')}
                  sx={canClients
                    ? { textTransform: 'none' }
                    : { backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
                >
                  {t('erp.home.quickActions.newQuotation')}
                </Button>
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/erp/quotations')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('erp.home.quickActions.viewQuotations')}
                </Button>
              </>
            )}
          </Box>
        </>
      )}

      {/* Trazabilidad end-to-end por RUT (criterio 14) — solo roles completos */}
      {canTrace && <ErpTraceabilitySearch />}
    </Box>
  );
};

export default ErpHomeLayout;
